/**
 * Parent-side logo editor. Owns all durable logo mutations and sends
 * live-preview-only bridge messages for logo wrappers and inner image variants.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import {
  LOGO_WRAPPER_SELECTOR,
  applyImageAttrs,
  applyLogoAspectRatio,
  aspectRatioFromDimensions,
  ensureLogoImageTargets,
  findLogoWrappers,
  isLogoWrapper
} from './logo-dom.js';
import { bridge } from './bridge.js';
import { escapeForAttrSelector, injectEditableAttrs, stripTempAttrs } from './editable-utils.js';
import {
  clampLogoHeightPx,
  clampLogoOpticalScale,
  fitLogoHeightPx,
  logoAnchorOverrideForHeight,
  logoAspectNumber,
  logoSizeLimits
} from './logo-geometry.js';
import { normalizeLogoSlots } from './logo-normalizer.js';
import {
  computeElementOverrides,
  getElementOverridesForViewport,
  resolveSectionByInstance
} from './section-utils.js';
import { formatSectionName } from '../shared/section-display-name.js';
import { showImagePicker } from './image-picker.js';
import { openSvgLibraryForPick } from './image-editor.js';
import { snapshotReplacementTarget, validateReplacementTarget } from './replace-dispatcher.js';
import { readIntrinsicDimensions } from './logo-measure.js';
import { isDomTarget, targetRefFor } from '../shared/target-ref.js';

const LOGO_VAR_BASES = {
  tx: '--pb-logo-tx',
  ty: '--pb-logo-ty',
  txPct: '--pb-logo-tx-pct',
  tyPct: '--pb-logo-ty-pct',
  scale: '--pb-logo-scale',
  slotWidth: '--pb-logo-slot-width',
  slotHeight: '--pb-logo-slot-height'
};
const LOGO_BREAKPOINTS = new Set(['mobile', 'tablet', 'desktop']);
const LOGO_VAR_KEYS = new Set(Object.values(LOGO_VAR_BASES));
for (const baseName of Object.values(LOGO_VAR_BASES)) {
  for (const breakpoint of LOGO_BREAKPOINTS) {
    LOGO_VAR_KEYS.add(`${baseName}-${breakpoint}`);
  }
}

const LOGO_HOVER_PRESETS = new Set(['none', 'scale', 'lift', 'rotate', 'shimmer', 'color-shift']);

/**
 * The structural shape of a logo wrapper, independent of `data-pb-editable`.
 *
 * `parseLogoHTML` only injects editable attrs when the eid lookup misses, so a
 * sibling sweep keyed on `[data-pb-editable="logo"]` would silently find nothing
 * on the common path. `normalizeLogoSlots` has already run by then, so every
 * logo wrapper is guaranteed to carry a `.logo-slot`.
 */
// LOGO_WRAPPER_SELECTOR, isLogoWrapper, findLogoWrappers and
// applyLogoAspectRatio now live in ./logo-dom.js — see its header for why the
// two predicates there differ.

/**
 * The placeholder `createDefaultClientInfo` seeds when a project has no name.
 * Alt text of "Untitled logo" is not worth writing.
 */
const PLACEHOLDER_CLIENT_NAME = 'untitled';

let activeLogoEdit = null;

function retireLogoTarget({ instanceIds = [] } = {}) {
  if (instanceIds.includes(activeLogoEdit?.instanceId)) activeLogoEdit = null;
}

function captureLogoReplacement() {
  const owner = activeLogoEdit;
  const captured = snapshotReplacementTarget(owner);
  return captured.ok
    ? () => activeLogoEdit === owner && validateReplacementTarget(captured.target).ok
    : () => false;
}

export function initLogoEditor() {
  eventBus.on('editable-ids:repaired', retireLogoTarget);
  eventBus.on('iframe:element-clicked', onElementClicked);
}

export function destroyLogoEditor() {
  eventBus.off('editable-ids:repaired', retireLogoTarget);
  eventBus.off('iframe:element-clicked', onElementClicked);
  activeLogoEdit = null;
}

function onElementClicked(data) {
  if (!isDomTarget(data)) return;
  if (data?.editableType !== 'logo') return;
  activeLogoEdit = data;
  eventBus.emit('editing:logo-selected', data);
}

export function setActiveLogoEdit(data) {
  activeLogoEdit =
    data && data.editableType === 'logo'
      ? { ...data, kind: data.kind || 'logo', targetRef: targetRefFor(data) }
      : null;
}

export function getActiveLogoEdit() {
  return activeLogoEdit;
}

export function getActiveLogoBreakpoint(data = activeLogoEdit) {
  return normalizeLogoBreakpoint(
    data?.logoBreakpoint || data?.logoVars?.breakpoint || data?.breakpoint
  );
}

function normalizeLogoBreakpoint(value) {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return LOGO_BREAKPOINTS.has(raw) ? raw : 'desktop';
}

function sanitizeFilename(name) {
  const cleaned = String(name || 'logo.svg')
    .trim()
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return cleaned && /\.svg$/i.test(cleaned) ? cleaned : `${cleaned || 'logo'}.svg`;
}

/**
 * Alt text for a REPLACEMENT logo.
 *
 * This used to return the outgoing wrapper's `aria-label`, falling back to the
 * outgoing image's `alt` — i.e. the template's own words. On a real template
 * that shipped `back to home` as the screen-reader description of somebody
 * else's brand mark, and on a white-label product it carries the previous
 * client's copy into the new site. A replacement is a NEW asset, so its
 * description comes from THIS project, never from the mark it displaced.
 */
function getLogoReplacementAlt() {
  const name = String(store.getState()?.clientInfo?.name || '').trim();
  if (!name || name.toLowerCase() === PLACEHOLDER_CLIENT_NAME) return '';
  return `${name} logo`;
}

/**
 * The replacement's true proportions, as an `aspect-ratio` value.
 *
 * `applyLogoSource` has always MEASURED this (`readIntrinsicDimensions`) and
 * stamped it on the `<img>`, then thrown it away — the slot kept the shape of
 * the logo it replaced, so every new mark was letterboxed into a box built for
 * the old one. Measured across ten template headers: painted fill fell from a
 * mean of 80% to 41% for a wide mark and 17% for a square one.
 */
const ROOT_FONT_PX = 16;

/** The active slot's authored aspect ratio, read from the section HTML. */
function readActiveSlotAspectRatio() {
  if (!activeLogoEdit) return '';
  const resolved = resolveSectionByInstance(activeLogoEdit.instanceId);
  if (!resolved?.section) return '';
  const parsed = parseLogoHTML(resolved.section.html, activeLogoEdit);
  const slot = parsed?.wrapper?.querySelector?.('.logo-slot');
  return slot?.style.getPropertyValue('--pb-logo-aspect-ratio') || '';
}

function cssLengthToPx(value) {
  const raw = String(value ?? '').trim();
  const numeric = Number.parseFloat(raw);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const isRem = /rem\s*$/i.test(raw) || /^-?[0-9]*\.?[0-9]+$/.test(raw);
  return isRem ? numeric * ROOT_FONT_PX : numeric;
}

/** Clamp one requested height against the current band and asset ratio. */
export function clampActiveLogoHeight(value, data = activeLogoEdit) {
  const px = cssLengthToPx(value);
  if (!px) return { value: String(value || ''), px: null, percent: null, clamped: false };
  const ratio = logoAspectNumber(readActiveSlotAspectRatio() || data?.logoVars?.aspectRatio);
  const result = clampLogoHeightPx(px, data || {}, ratio);
  return {
    ...result,
    value: `${parseFloat(result.px.toFixed(2))}px`
  };
}

function logoVarName(kind, breakpoint = null) {
  const baseName = LOGO_VAR_BASES[kind];
  if (!baseName) return '';
  const normalized = breakpoint ? normalizeLogoBreakpoint(breakpoint) : '';
  return normalized ? `${baseName}-${normalized}` : baseName;
}

function readSlotVar(slot, kind, fallback, breakpoint) {
  if (!slot) return fallback;
  const scopedName = logoVarName(kind, breakpoint);
  const baseName = logoVarName(kind);
  const scoped = scopedName ? slot.style.getPropertyValue(scopedName) : '';
  if (scoped) return scoped;
  const base = baseName ? slot.style.getPropertyValue(baseName) : '';
  return base || fallback;
}

function fractionToPercent(value) {
  const parsed = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(parsed)) return '';
  return `${parseFloat((parsed * 100).toFixed(4))}%`;
}

export async function replaceLogo({ variant = 'all', applyToBoth = true } = {}) {
  if (!activeLogoEdit) return Promise.resolve();
  const isCurrent = captureLogoReplacement();
  if (!isCurrent()) return Promise.resolve();
  const resolved = resolveSectionByInstance(activeLogoEdit.instanceId);
  const current = resolved?.section
    ? getLogoInfoFromHTML(resolved.section.html, activeLogoEdit)?.images?.[0]?.src || ''
    : '';
  const context = describeLogoPickerTarget(resolved?.section);
  return showImagePicker(
    async ({ file, url, source, author, authorUrl }) => {
      if (!isCurrent()) return;
      if (file) {
        await applyLogoFile({ file, variant, applyToBoth, meta: { source, author, authorUrl } });
        return;
      }
      if (url) {
        await applyLogoUrl({ url, variant, applyToBoth });
        return;
      }
      throw new Error('No logo payload provided');
    },
    { currentImageUrl: current, ...(context ? { context } : {}) }
  );
}

function describeLogoPickerTarget(section) {
  const name = formatSectionName(section);
  return name && name !== 'Section' ? `Replacing a logo in ${name}` : '';
}

export async function replaceLogoFromSvgLibrary({ variant = 'all', applyToBoth = true } = {}) {
  if (!activeLogoEdit) return Promise.resolve();
  const isCurrent = captureLogoReplacement();
  if (!isCurrent()) return Promise.resolve();
  return openSvgLibraryForPick({
    onPick: async (markup, entry = {}) => {
      if (!isCurrent()) return;
      try {
        const name = sanitizeFilename(`${entry?.name || 'logo'}.svg`);
        const file = new File([markup], name, { type: 'image/svg+xml' });
        await applyLogoFile({
          file,
          variant,
          applyToBoth,
          meta: { source: 'SVG Library' }
        });
      } catch (err) {
        console.error('[LogoEditor] SVG library logo replace failed:', err);
        eventBus.emit('editing:error', { message: 'Could not replace logo from SVG library' });
      }
    }
  });
}

export async function applyLogoFile({
  file,
  variant = 'all',
  applyToBoth = true,
  meta = null
} = {}) {
  if (!activeLogoEdit || !file) return null;
  const isCurrent = captureLogoReplacement();
  if (!isCurrent()) return null;
  const validity = validateReplacementTarget(activeLogoEdit, { mimeType: file.type || '' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return null;
  }

  const assetId = await store.assets.store(file, meta || undefined);
  const assetUri = `pb-asset://${assetId}`;
  const dimensions = await readIntrinsicDimensions(file);
  const alt = getLogoReplacementAlt();
  const mimeType = file.type || '';
  const previewUrl = await getPreviewUrlForFile(file, assetId);

  if (!isCurrent()) return null;
  return applyLogoSource({
    src: assetUri,
    previewSrc: previewUrl || assetUri,
    alt,
    assetId,
    mimeType,
    width: dimensions?.width,
    height: dimensions?.height,
    variant,
    applyToBoth,
    actionLabel: 'Replace logo',
    filename: file.name || ''
  });
}

export async function setLogoSourceFromLibrary({
  assetId,
  variant = 'all',
  applyToBoth = true
} = {}) {
  if (!activeLogoEdit || !assetId) return null;
  const isCurrent = captureLogoReplacement();
  if (!isCurrent()) return null;

  const record = typeof store.assets.get === 'function' ? await store.assets.get(assetId) : null;
  const blob = record?.blob || null;
  const filename = record?.filename || 'Logo';
  const mimeType = record?.mime || blob?.type || '';
  const dimensions = blob ? await readIntrinsicDimensions(blob, filename) : null;
  let previewUrl;
  try {
    previewUrl =
      typeof store.assets.getBlobUrl === 'function'
        ? (await store.assets.getBlobUrl(assetId)) || ''
        : '';
  } catch {
    previewUrl = '';
  }

  if (!isCurrent()) return null;
  return applyLogoSource({
    src: `pb-asset://${assetId}`,
    previewSrc: previewUrl || `pb-asset://${assetId}`,
    alt: getLogoReplacementAlt(),
    assetId,
    mimeType,
    width: dimensions?.width,
    height: dimensions?.height,
    variant,
    applyToBoth,
    actionLabel: 'Replace logo',
    filename
  });
}

export async function applyLogoUrl({ url, variant = 'all', applyToBoth = true } = {}) {
  if (!activeLogoEdit || !url) return null;
  const validity = validateReplacementTarget(activeLogoEdit);
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return null;
  }
  const alt = getLogoReplacementAlt();
  return applyLogoSource({
    src: url,
    previewSrc: url,
    alt,
    assetId: '',
    mimeType: '',
    variant,
    applyToBoth,
    actionLabel: 'Replace logo',
    filename: inferNameFromUrl(url)
  });
}

export function setLogoLabel({ ariaLabel } = {}) {
  if (!activeLogoEdit) return null;
  const label = String(ariaLabel || '');
  const result = updateActiveLogo((wrapper) => {
    if (label) wrapper.setAttribute('aria-label', label);
    else wrapper.removeAttribute('aria-label');
    for (const img of wrapper.querySelectorAll('.logo-slot img')) {
      img.setAttribute('alt', label);
    }
  }, 'Edit logo description');
  if (!result) return null;

  sendLogoVars({ wrapperAttrs: { 'aria-label': label } });
  sendLogoImage({ variant: 'all', alt: label });
  return result;
}

export function setLogoHref({ href } = {}) {
  if (!activeLogoEdit) return null;
  const nextHref = String(href || '');
  const result = updateActiveLogo((wrapper) => {
    if (nextHref) wrapper.setAttribute('href', nextHref);
    else wrapper.removeAttribute('href');
  }, 'Edit logo link');
  if (!result) return null;

  sendLogoVars({ wrapperAttrs: { href: nextHref } });
  return result;
}

export function setLogoHover(preset = 'none') {
  if (!activeLogoEdit) return null;
  const hover = LOGO_HOVER_PRESETS.has(preset) ? preset : 'none';
  const result = updateActiveLogo((wrapper) => {
    wrapper.setAttribute('data-logo-hover', hover);
    if (hover !== 'none') {
      wrapper.querySelector('.logo-slot')?.setAttribute('data-logo-engaged', 'true');
    }
  }, 'Change logo hover');
  if (!result) return null;

  sendLogoVars({
    wrapperAttrs: { 'data-logo-hover': hover },
    slotAttrs: hover !== 'none' ? { 'data-logo-engaged': 'true' } : null
  });
  return result;
}

export function setLogoTransform({
  tx,
  ty,
  scale,
  slotWidth,
  slotHeight,
  breakpoint,
  applyToAll = false
} = {}) {
  if (!activeLogoEdit) return null;
  const activeBreakpoint = applyToAll
    ? null
    : getActiveLogoBreakpoint({
        ...activeLogoEdit,
        logoBreakpoint: breakpoint || activeLogoEdit.logoBreakpoint
      });
  const slotStyle = {};
  if (tx != null) {
    const normalized = normalizeNumberString(tx);
    slotStyle[logoVarName('tx', activeBreakpoint)] = normalized;
    slotStyle[logoVarName('txPct', activeBreakpoint)] = fractionToPercent(normalized);
  }
  if (ty != null) {
    const normalized = normalizeNumberString(ty);
    slotStyle[logoVarName('ty', activeBreakpoint)] = normalized;
    slotStyle[logoVarName('tyPct', activeBreakpoint)] = fractionToPercent(normalized);
  }
  if (scale != null) {
    const currentTx = tx ?? activeLogoEdit?.logoVars?.tx ?? 0;
    const currentTy = ty ?? activeLogoEdit?.logoVars?.ty ?? 0;
    const optical = clampLogoOpticalScale(activeLogoEdit || {}, scale, currentTx, currentTy);
    slotStyle[logoVarName('scale', activeBreakpoint)] = normalizeNumberString(optical.scale);
  }
  if (slotWidth != null)
    slotStyle[logoVarName('slotWidth', activeBreakpoint)] = normalizeSlotWidth(slotWidth);

  // Authoring a HEIGHT also frees the width, and that pairing is load-bearing.
  //
  // `.logo-slot` sets both axes, and an element with an explicit width AND height
  // ignores `aspect-ratio` outright — which is why LG-0's correct
  // `--pb-logo-aspect-ratio` was inert on 7 of 10 measured headers. Freeing the
  // width lets the ratio derive it from the height.
  //
  // Why the width default stays `100%` in the runtime CSS rather than becoming
  // `auto` globally: the derivation direction is a property of the ANCHOR, not of
  // the slot. A flex header cell has a definite height and an auto width, so
  // `width:auto` derives correctly; `codestitch.app`'s footer anchor is
  // `display:block; width:13.375em; height:auto` — definite WIDTH — so `auto`
  // leaves nothing to derive from, and in `body.dark-mode` (where the only
  // in-flow media child is hidden) the slot collapses to 0x0. The corpus gate
  // caught exactly that. Pairing the two here means `auto` only ever appears
  // where a definite height exists BY CONSTRUCTION, because the user just set one.
  let anchorChanges = null;
  let sizedAttr = null;
  if (slotHeight != null) {
    const clamped = clampActiveLogoHeight(slotHeight);
    const normalizedHeight = slotHeight === '' ? '' : normalizeSlotWidth(clamped.value);
    slotStyle[logoVarName('slotHeight', activeBreakpoint)] = normalizedHeight;
    if (slotWidth == null) {
      slotStyle[logoVarName('slotWidth', activeBreakpoint)] = normalizedHeight ? 'auto' : '';
    }
    // The template anchor is often the hard-coded box. Sizing only its child is
    // therefore inert on most corpus headers. Use the existing element override
    // channel so preview, responsive CSS, export and undo all share one model.
    anchorChanges = logoAnchorOverrideForHeight(normalizedHeight);
    sizedAttr = normalizedHeight ? 'true' : '';
  }

  const result = updateActiveLogoAndOverrides(
    (wrapper) => {
      const slot = wrapper.querySelector('.logo-slot');
      if (!slot) return;
      if (Object.keys(slotStyle).length) slot.setAttribute('data-logo-engaged', 'true');
      if (sizedAttr === 'true') slot.setAttribute('data-logo-sized', 'true');
      else if (sizedAttr === '') slot.removeAttribute('data-logo-sized');
      for (const [name, value] of Object.entries(slotStyle)) {
        if (!LOGO_VAR_KEYS.has(name)) continue;
        if (value) slot.style.setProperty(name, value);
        else slot.style.removeProperty(name);
      }
    },
    slotHeight != null ? 'Resize logo' : 'Move logo',
    anchorChanges,
    activeBreakpoint
  );
  if (!result) return null;

  sendLogoVars({
    wrapperStyle: anchorChanges ? anchorOverrideToStyle(anchorChanges) : null,
    slotStyle,
    slotAttrs: Object.keys(slotStyle).length
      ? {
          'data-logo-engaged': 'true',
          ...(sizedAttr != null ? { 'data-logo-sized': sizedAttr } : {})
        }
      : null
  });
  return result;
}

export function getActiveLogoFitInfo(data = activeLogoEdit) {
  const slotWidth = Number(data?.logoSlotRect?.width);
  const slotHeight = Number(data?.logoSlotRect?.height);
  const ratio = logoAspectNumber(readActiveSlotAspectRatio() || data?.logoVars?.aspectRatio);
  const slotRatio = slotWidth > 0 && slotHeight > 0 ? slotWidth / slotHeight : null;
  const fill = ratio && slotRatio ? Math.min(ratio / slotRatio, slotRatio / ratio) * 100 : null;
  const limits = logoSizeLimits(data || {}, ratio);
  return {
    fillPercent: Number.isFinite(fill) ? Math.max(0, Math.min(100, fill)) : null,
    heightPx: slotHeight > 0 ? slotHeight : null,
    bandPercent:
      slotHeight > 0 && limits.usableHeightPx
        ? Math.max(0, Math.min(100, (slotHeight / limits.usableHeightPx) * 100))
        : null,
    maxHeightPx: Number.isFinite(limits.maxPx) ? limits.maxPx : null
  };
}

/** Expand the logo to the largest contained size its current band permits. */
export function fitActiveLogoToSpace({ applyToAll = false } = {}) {
  if (!activeLogoEdit) return false;
  const info = getActiveLogoFitInfo();
  if (!Number.isFinite(info.maxHeightPx) || info.maxHeightPx <= 0) return false;
  store.beginTransaction('Fit logo to space');
  try {
    setLogoTransform({ slotHeight: `${info.maxHeightPx}px`, applyToAll });
    store.commitTransaction();
  } catch (error) {
    store.abortTransaction();
    throw error;
  }
  eventBus.emit('editing:logo-mutated', { label: 'Logo fitted' });
  return true;
}

export function getLogoInfoFromHTML(sectionHtml, data = activeLogoEdit) {
  if (!sectionHtml || !data) return null;
  const parsed = parseLogoHTML(sectionHtml, data);
  if (!parsed?.wrapper) return null;
  return readLogoInfo(parsed.wrapper, data);
}

function applyLogoSource({
  src,
  previewSrc,
  alt,
  assetId,
  mimeType,
  width,
  height,
  variant,
  applyToBoth,
  actionLabel,
  filename
}) {
  if (!activeLogoEdit || !src) return null;
  const validity = validateReplacementTarget(activeLogoEdit, { mimeType: mimeType || '' });
  if (!validity.ok) {
    eventBus.emit('editing:error', { message: `Replacement skipped: ${validity.reason}` });
    return null;
  }
  const targetVariant = applyToBoth ? 'all' : variant || 'all';
  const aspectRatio = aspectRatioFromDimensions(width, height);
  const fitted = aspectRatio
    ? fitLogoHeightPx(
        // Replacement is shrink-only inside the template's CURRENT anchor.
        // Explicit user sizing may grow that anchor within the wider header
        // band; an automatic replacement must not make that decision for them.
        {
          ...activeLogoEdit,
          logoBoundaryRect: activeLogoEdit.rect || activeLogoEdit.logoBoundaryRect
        },
        aspectRatio
      )
    : null;

  // A section commonly holds more than one logo slot — a desktop mark and the
  // mobile-menu mark are the usual pair, and 83 corpus slots hold more than one
  // media child. "Set my logo" is ONE intention, so a replacement lands on every
  // slot in the section. Only the SOURCE fans out: href, hover, description and
  // transform stay single-target, because those are per-placement decisions.
  const touchedEids = [];
  let result;
  store.beginTransaction(actionLabel || 'Replace logo');
  try {
    result = updateActiveLogo((wrapper, root) => {
      // The active wrapper is included explicitly rather than assumed: `isLogoWrapper`
      // also accepts a bare `[data-pb-editable="logo"]`, which need not match the
      // structural anchor selector the sweep uses.
      const targets = findLogoWrappers(root);
      if (wrapper && !targets.includes(wrapper)) targets.unshift(wrapper);

      for (const target of targets) {
        for (const img of ensureLogoImageTargets(target, targetVariant)) {
          applyImageAttrs(img, { src, alt, assetId, mimeType, width, height });
        }
        applyLogoAspectRatio(target, aspectRatio);
        target.removeAttribute('data-pb-logo-source-locked');
        const eid = target.getAttribute('data-pb-eid') || '';
        if (eid) touchedEids.push(eid);
      }
    }, actionLabel || 'Replace logo');
    if (!result) {
      store.abortTransaction();
      return null;
    }
    // Replacement is shrink-only: preserve the existing visual height unless
    // the new aspect would collide with a blocker or leave the header band.
    if (fitted?.px) setLogoTransform({ slotHeight: `${fitted.px}px` });
    store.commitTransaction();
  } catch (error) {
    store.abortTransaction();
    throw error;
  }

  const imagePayload = {
    variant: targetVariant,
    src: previewSrc || src,
    alt,
    width,
    height,
    assetId,
    mimeType
  };
  const slotCount = Math.max(1, touchedEids.length);

  // The bridge resolves ONE element per message (`findEditableByPath`), so the
  // live preview needs one message per slot rather than a new multi-target
  // message type. The aspect ratio rides `update-logo-vars` because that is the
  // handler that owns slot custom properties.
  if (touchedEids.length) {
    for (const eid of touchedEids) {
      sendLogoImage({ ...imagePayload, eid });
      if (aspectRatio) sendLogoVars({ eid, slotStyle: { '--pb-logo-aspect-ratio': aspectRatio } });
    }
  } else {
    sendLogoImage(imagePayload);
    if (aspectRatio) sendLogoVars({ slotStyle: { '--pb-logo-aspect-ratio': aspectRatio } });
  }

  eventBus.emit('editing:logo-replaced', {
    assetId,
    filename: filename || '',
    kind: 'logo',
    variant: targetVariant,
    slotCount
  });
  return result;
}

function updateActiveLogo(mutator, actionLabel) {
  const resolved = activeLogoEdit ? resolveSectionByInstance(activeLogoEdit.instanceId) : null;
  if (!resolved) return null;

  const parsed = parseLogoHTML(resolved.section.html, activeLogoEdit);
  if (!parsed?.root || !parsed.wrapper) return null;

  mutator(parsed.wrapper, parsed.root, parsed.doc);
  stripTempAttrs(parsed.root);
  const updatedHtml = parsed.root.innerHTML;
  store.update(resolved.path, updatedHtml, actionLabel);
  return updatedHtml;
}

function anchorOverrideToStyle(changes = {}) {
  return {
    width: changes.width || '',
    height: changes.height || '',
    'max-width': changes.maxWidth || ''
  };
}

/**
 * Mutate the normalized logo HTML and its anchor override in one store update.
 * A scrub may call this every animation frame, but the surrounding transaction
 * still collapses the gesture to one undo entry.
 */
function updateActiveLogoAndOverrides(mutator, actionLabel, anchorChanges, viewport) {
  const resolved = activeLogoEdit ? resolveSectionByInstance(activeLogoEdit.instanceId) : null;
  if (!resolved) return null;

  const parsed = parseLogoHTML(resolved.section.html, activeLogoEdit);
  if (!parsed?.root || !parsed.wrapper) return null;
  mutator(parsed.wrapper, parsed.root, parsed.doc);
  stripTempAttrs(parsed.root);
  const updatedHtml = parsed.root.innerHTML;

  let overrides = resolved.section.overrides || {};
  const eid = parsed.wrapper.getAttribute('data-pb-eid') || activeLogoEdit.eid || '';
  if (anchorChanges && eid) {
    overrides = computeElementOverrides(overrides, eid, anchorChanges, viewport).overrides;
  }

  store.update(
    resolved.sectionPath,
    {
      ...resolved.section,
      html: updatedHtml,
      overrides: Object.keys(overrides).length ? overrides : null
    },
    actionLabel
  );

  if (anchorChanges && eid) {
    for (const field of Object.keys(anchorChanges)) {
      eventBus.emit('theme:override-changed', {
        scope: 'element',
        instanceId: activeLogoEdit.instanceId,
        eid,
        field,
        viewport,
        source: 'logo-controls'
      });
    }
  }
  return updatedHtml;
}

/** Restore bridge-only styles from durable state after an aborted gesture. */
export function syncActiveLogoPreviewFromState() {
  const resolved = activeLogoEdit ? resolveSectionByInstance(activeLogoEdit.instanceId) : null;
  if (!resolved?.section) return false;
  const parsed = parseLogoHTML(resolved.section.html, activeLogoEdit);
  if (!parsed?.wrapper) return false;
  const slot = parsed.wrapper.querySelector('.logo-slot');
  const slotStyle = {};
  for (const name of LOGO_VAR_KEYS) slotStyle[name] = slot?.style.getPropertyValue(name) || '';
  slotStyle['--pb-logo-aspect-ratio'] =
    slot?.style.getPropertyValue('--pb-logo-aspect-ratio') || '';

  const eid = parsed.wrapper.getAttribute('data-pb-eid') || activeLogoEdit.eid || '';
  const viewport = getActiveLogoBreakpoint();
  const props = getElementOverridesForViewport(resolved.section, eid, viewport);
  sendLogoVars({
    wrapperStyle: anchorOverrideToStyle(props),
    slotStyle,
    slotAttrs: {
      'data-logo-engaged': slot?.getAttribute('data-logo-engaged') === 'true' ? 'true' : '',
      'data-logo-sized': slot?.getAttribute('data-logo-sized') === 'true' ? 'true' : ''
    }
  });
  return true;
}

function parseLogoHTML(sectionHtml, data) {
  const doc = new DOMParser().parseFromString(`<div>${sectionHtml || ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;
  normalizeLogoSlots(root);

  let wrapper = null;
  if (data?.eid) {
    wrapper = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(data.eid)}"]`);
  }
  if (!isLogoWrapper(wrapper)) {
    injectEditableAttrs(root);
    const editables = Array.from(root.querySelectorAll('[data-pb-editable]'));
    wrapper = editables[data?.editableIndex || 0] || null;
  }
  if (!isLogoWrapper(wrapper)) return null;
  return { doc, root, wrapper };
}

function readLogoInfo(wrapper, data = activeLogoEdit) {
  const slot = wrapper.querySelector('.logo-slot');
  const images = Array.from(wrapper.querySelectorAll('.logo-slot img'));
  const hasLight = images.some((img) => img.classList.contains('light'));
  const hasDark = images.some((img) => img.classList.contains('dark'));
  const breakpoint = getActiveLogoBreakpoint(data);
  const variants =
    hasLight || hasDark
      ? [...(hasLight ? ['light'] : []), ...(hasDark ? ['dark'] : [])]
      : images.length > 1
        ? ['light', 'dark']
        : ['all'];

  return {
    ariaLabel: wrapper.getAttribute('aria-label') || '',
    href: wrapper.getAttribute('href') || '',
    hover: wrapper.getAttribute('data-logo-hover') || 'none',
    sourceLocked:
      wrapper.getAttribute('data-pb-logo-source-locked') === '1' ||
      !!wrapper.querySelector('.logo-slot svg'),
    variants,
    images: images.map((img, index) => ({
      index,
      variant: img.classList.contains('dark')
        ? 'dark'
        : img.classList.contains('light')
          ? 'light'
          : index === 1
            ? 'dark'
            : 'light',
      src: img.getAttribute('src') || '',
      alt: img.getAttribute('alt') || '',
      width: img.getAttribute('width') || '',
      height: img.getAttribute('height') || '',
      assetId: img.getAttribute('data-pb-asset') || ''
    })),
    breakpoint,
    vars: {
      tx: readSlotVar(slot, 'tx', '0', breakpoint),
      ty: readSlotVar(slot, 'ty', '0', breakpoint),
      scale: readSlotVar(slot, 'scale', '1', breakpoint),
      slotWidth: readSlotVar(slot, 'slotWidth', '', breakpoint),
      slotHeight: readSlotVar(slot, 'slotHeight', '', breakpoint)
    }
  };
}

/**
 * Address one logo slot on the wire.
 *
 * `findEditableByPath` prefers `eid` and FALLS BACK to `editableIndex`. That
 * fallback is right for the active slot and wrong for a sibling: an eid that
 * misses would resolve to whatever sits at the active slot's index and mutate
 * the wrong element. So an explicit eid travels ALONE — a miss must resolve to
 * nothing, never to a neighbour.
 */
function logoTargetPath(eid) {
  if (eid) return { instanceId: activeLogoEdit.instanceId, eid };
  return {
    instanceId: activeLogoEdit.instanceId,
    editableIndex: activeLogoEdit.editableIndex,
    eid: activeLogoEdit.eid || ''
  };
}

function sendLogoVars({
  wrapperAttrs = null,
  wrapperStyle = null,
  slotAttrs = null,
  slotStyle = null,
  eid = ''
} = {}) {
  if (!activeLogoEdit) return;
  bridge.send('update-logo-vars', {
    ...logoTargetPath(eid),
    wrapperAttrs,
    wrapperStyle,
    slotAttrs,
    slotStyle
  });
}

function sendLogoImage({ eid = '', ...payload } = {}) {
  if (!activeLogoEdit) return;
  bridge.send('update-logo-image', {
    ...logoTargetPath(eid),
    ...payload
  });
}

async function getPreviewUrlForFile(file, assetId) {
  try {
    const dataUrl = await fileToDataUrl(file);
    if (dataUrl) return dataUrl;
  } catch {}
  try {
    return typeof store.assets.getBlobUrl === 'function'
      ? (await store.assets.getBlobUrl(assetId)) || ''
      : '';
  } catch {
    return '';
  }
}

function fileToDataUrl(file) {
  if (typeof FileReader !== 'function') return Promise.resolve('');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function normalizeNumberString(value) {
  const num = Number(value);
  return Number.isFinite(num) ? String(num) : String(value || '');
}

function normalizeSlotWidth(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}rem`;
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^-?[0-9]*\.?[0-9]+$/.test(raw)) return `${raw}rem`;
  return raw;
}

function inferNameFromUrl(url, fallback = 'Logo') {
  try {
    const parsed = new URL(url);
    const rawName = parsed.pathname.split('/').pop() || '';
    const decoded = decodeURIComponent(rawName).trim();
    return decoded || fallback;
  } catch {
    return fallback;
  }
}
